import React, { useState } from 'react';
import Chart from 'react-apexcharts';
import { Helmet } from 'react-helmet';

const PetFoodPortionCalculator = () => {
  const [petType, setPetType] = useState('');
  const [weight, setWeight] = useState('');
  const [age, setAge] = useState('');
  const [activity, setActivity] = useState('');
  const [result, setResult] = useState(null);

  const calculatePortion = () => {
    if (!petType || !weight || !age || !activity) {
      alert('Please fill in all fields!');
      return;
    }

    const weightNum = parseFloat(weight);
    let dailyCalories = 0;

    // Base calculation based on weight
    dailyCalories = weightNum * 30 + 70;

    // Adjust based on pet type
    switch (petType) {
      case 'dog':
        dailyCalories *= 1.2;
        break;
      case 'cat':
        dailyCalories *= 1.1;
        break;
      case 'rabbit':
        dailyCalories *= 0.8;
        break;
      case 'bird':
        dailyCalories *= 0.5;
        break;
      case 'hamster':
        dailyCalories *= 0.3;
        break;
      default:
        break;
    }

    // Adjust based on age
    switch (age) {
      case 'young':
        dailyCalories *= 1.2;
        break;
      case 'senior':
        dailyCalories *= 0.8;
        break;
      default:
        break;
    }

    // Adjust based on activity level
    switch (activity) {
      case 'low':
        dailyCalories *= 0.9;
        break;
      case 'high':
        dailyCalories *= 1.2;
        break;
      default:
        break;
    }

    const dailyGrams = (dailyCalories / 3.5).toFixed(2);
    setResult({
      calories: dailyCalories.toFixed(2),
      grams: dailyGrams,
      meals: Math.round(dailyGrams / 2)
    });
  };

  return (
    <>
      <Helmet>
        <title>Pet Food Portion Calculator - Calculate Perfect Pet Food Portions</title>
        <meta name="description" content="Calculate the perfect food portions for your pets based on their type, weight, age, and activity level. Get accurate daily calorie and food portion recommendations for dogs, cats, rabbits, birds, and hamsters." />
        <meta property="og:title" content="Pet Food Portion Calculator" />
        <meta property="og:description" content="Calculate the perfect food portions for your pets based on their type, weight, age, and activity level. Get accurate daily calorie and food portion recommendations for dogs, cats, rabbits, birds, and hamsters." />
        <meta property="og:image" content="https://your-domain.com/pet-food-calculator-preview.png" />
        <meta property="og:url" content="https://your-domain.com/pet-food-calculator" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Pet Food Portion Calculator" />
        <meta name="twitter:description" content="Calculate the perfect food portions for your pets based on their type, weight, age, and activity level. Get accurate daily calorie and food portion recommendations for dogs, cats, rabbits, birds, and hamsters." />
        <meta name="twitter:image" content="https://your-domain.com/pet-food-calculator-preview.png" />
      </Helmet>

      <div className="max-w-4xl mx-auto p-6 bg-gradient-to-br from-green-50 to-white rounded-lg shadow-lg">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-green-600 mb-2">
            🐾 Pet Food Portion Calculator 🐾
          </h1>
          <p className="text-gray-600">
            Calculate the perfect food portions for your pets 🐰
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Pet Type 🐹
            </label>
            <select 
              value={petType}
              onChange={(e) => setPetType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="">Select pet type</option>
              <option value="dog">Dog</option>
              <option value="cat">Cat</option>
              <option value="rabbit">Rabbit</option>
              <option value="bird">Bird</option>
              <option value="hamster">Hamster</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Pet's Weight (kg)
            </label>
            <input
              type="number"
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder="Enter pet's weight"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Age Group
            </label>
            <select 
              value={age}
              onChange={(e) => setAge(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="">Select age group</option>
              <option value="young">Young</option>
              <option value="adult">Adult</option>
              <option value="senior">Senior</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Activity Level
            </label>
            <select 
              value={activity}
              onChange={(e) => setActivity(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="">Select activity level</option>
              <option value="low">Low</option>
              <option value="moderate">Moderate</option>
              <option value="high">High</option>
            </select>
          </div>
        </div>

        <button
          onClick={calculatePortion}
          className="w-full bg-gradient-to-r from-green-600 to-green-700 text-red py-2 px-4 rounded-md hover:from-green-700 hover:to-green-800 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
        >
          Calculate Pet Food Portion
        </button>

        {result && (
          <div className="mt-8">
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-green-700 mb-4">Recommended Daily Portion</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center p-4 bg-white rounded-lg shadow">
                  <h3 className="text-xl font-semibold text-green-600">Calories</h3>
                  <p className="text-2xl font-bold">{result.calories} kcal</p>
                </div>
                <div className="text-center p-4 bg-white rounded-lg shadow">
                  <h3 className="text-xl font-semibold text-green-600">Food Portion</h3>
                  <p className="text-2xl font-bold">{result.grams} g</p>
                </div>
                <div className="text-center p-4 bg-white rounded-lg shadow">
                  <h3 className="text-xl font-semibold text-green-600">Meals per Day</h3>
                  <p className="text-2xl font-bold">{result.meals} meals</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-xl font-semibold text-green-600 mb-4">Nutrition Breakdown</h3>
              <Chart
                options={{
                  chart: {
                    type: 'donut',
                    width: 200,
                  },
                  labels: ['Calories', 'Food Portion', 'Meals'],
                  colors: ['#4CAF50', '#2196F3', '#FF9800'],
                  legend: {
                    position: 'bottom',
                    fontSize: '14px',
                  },
                  plotOptions: {
                    pie: {
                      donut: {
                        size: '30%',
                        labels: {
                          show: true,
                          name: {
                            fontSize: '14px',
                          },
                          value: {
                            fontSize: '14px',
                          },
                        },
                      },
                    },
                  },
                  responsive: [
                    {
                      breakpoint: 480,
                      options: {
                        chart: {
                          width: 200,
                        },
                        legend: {
                          position: 'bottom',
                          fontSize: '12px',
                        },
                      },
                    },
                  ],
                }}
                series={[parseFloat(result.calories), parseFloat(result.grams), parseFloat(result.meals)]}
                type="donut"
              />
            </div>
          </div>
        )}

        <div className="mt-8 bg-green-50 p-4 rounded-lg">
          <h3 className="text-xl font-semibold mb-4">How to Use</h3>
          <div className="space-y-4">
            <div className="bg-white p-4 rounded-lg shadow">
              <h4 className="text-lg font-semibold mb-2">1. Select Your Pet</h4>
              <p>Choose from dogs, cats, rabbits, birds, or hamsters</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <h4 className="text-lg font-semibold mb-2">2. Enter Pet Details</h4>
              <p>Input your pet's weight, age group, and activity level</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <h4 className="text-lg font-semibold mb-2">3. Get Results</h4>
              <p>Click calculate to see recommended daily portions and calories</p>
            </div>
          </div>

          <h3 className="text-xl font-semibold mt-4 mb-4">Features & Benefits</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white p-4 rounded-lg shadow">
              <h4 className="text-lg font-semibold mb-2">Multiple Pet Support</h4>
              <ul className="list-disc pl-5 space-y-1">
                <li>Calculate for different pet types</li>
                <li>Customizable for each pet's needs</li>
                <li>Separate calculations for multiple pets</li>
              </ul>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <h4 className="text-lg font-semibold mb-2">Accurate Calculations</h4>
              <ul className="list-disc pl-5 space-y-1">
                <li>Based on scientific formulas</li>
                <li>Adjusted for age and activity level</li>
                <li>Provides comprehensive nutrition data</li>
              </ul>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <h4 className="text-lg font-semibold mb-2">User-Friendly Interface</h4>
              <ul className="list-disc pl-5 space-y-1">
                <li>Simple and intuitive design</li>
                <li>Easy to understand results</li>
                <li>Quick and accurate calculations</li>
              </ul>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <h4 className="text-lg font-semibold mb-2">Health Benefits</h4>
              <ul className="list-disc pl-5 space-y-1">
                <li>Prevents overfeeding and underfeeding</li>
                <li>Helps maintain ideal weight</li>
                <li>Provides balanced nutrition</li>
              </ul>
            </div>
          </div>

          <h3 className="text-xl font-semibold mt-4 mb-4">FAQ</h3>
          <div className="space-y-3">
            <div>
              <strong>Q: How accurate are these calculations?</strong>
              <p>These are general guidelines. Always consult with your veterinarian for specific dietary needs.</p>
            </div>
            <div>
              <strong>Q: Can I use this for multiple pets?</strong>
              <p>Yes! Just calculate separately for each pet based on their individual needs.</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PetFoodPortionCalculator;
